import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Code2, Download, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import DetailPageHeader from '@components/common/DetailPageHeader';
import ConfirmDialog from '@components/common/ConfirmDialog';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import { RZTable } from '@components/common/table/RZTable';
import { SqlView } from '@components/common/SqlView';
import { QueryTypeBadge, FilterValueInput } from '@components/features/queries';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import { useQuerySchema } from '@hooks/useQuerySchema';
import { useQueryExecution } from '@hooks/useQueryExecution';
import { useQueryList } from '@hooks/useQueryList';
import { usePermissions } from '@contexts/AuthContext';
import type { UserPermissions } from '@contexts/AuthContext';
import { queryService } from '@services';
import { useReferenceCatalog } from '@hooks/useReferenceCatalog';
import { getQueryFieldLabel } from '@utils/referenceCatalog';
import { getApiErrorMessage } from '@utils/apiError';
import { validateFilters } from '@utils/queryFilterValidation';
import { showToast } from '@utils/toast';
import { QUERY_PAGE_SIZES, OPERATORS_BY_FIELD_TYPE, OPERATOR_LABELS } from '@app-types';
import type {
  TableColumn,
  QueryResultRow,
  QueryCellValue,
  QueryFilter,
  QueryFilterOperator,
  QueryField,
  QueryFieldType
} from '@app-types';
import styles from './QueryResultsPage.module.css';

/**
 * Per-entity detail-link config (US-10): which column field(s) become links, the
 * route base, and the permission flag that must be true for the link to render.
 * When the user lacks the permission, the value falls back to plain text.
 */
interface EntityLinkConfig {
  path: string;
  fields: string[];
  permission: keyof UserPermissions;
}

const ENTITY_LINK: Record<string, EntityLinkConfig> = {
  contract: { path: '/contracts', fields: ['contractNumber'], permission: 'canAccessContracts' },
  supplier: { path: '/suppliers', fields: ['name'], permission: 'canAccessSuppliers' },
  assessment: { path: '/assessments', fields: ['name'], permission: 'canAccessAssessments' },
  meeting: { path: '/contract-assessment-meetings', fields: ['meetingId'], permission: 'isAdmin' }
  // Contact = roleless User: there is no per-contact detail page (no /users/:id route),
  // so its name cells stay plain text like a Divisions query — never a link (#901).
};

const displayValue = (value: QueryCellValue, entity?: string, field?: string): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const raw = String(value);
  if (entity && field) return getQueryFieldLabel(entity, field, raw);
  return raw;
};

const QueryResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryId = Number(id);
  useReferenceCatalog();

  const { entities: schemaEntities } = useQuerySchema();
  const { downloadCsv } = useQueryExecution(queryId);
  const permissions = usePermissions();
  // Delete lives here too (US-07 / #900): reuse the list hook's mutation, but keep
  // its list query disabled — this page doesn't render the list.
  const { deleteQuery, isDeleting } = useQueryList({}, false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(QUERY_PAGE_SIZES[0]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [isExporting, setIsExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSql, setShowSql] = useState(false);
  // Ad-hoc filters (US-08): a draft list edited in the toolbar, plus the applied
  // list that is actually sent to the execute endpoint. Not persisted to the query.
  const [draftFilters, setDraftFilters] = useState<QueryFilter[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<QueryFilter[]>([]);
  const [filterError, setFilterError] = useState<string | null>(null);
  // Stable per-row React keys for the ad-hoc filters list — filter objects carry
  // no id of their own and can be structurally identical, so array index is the
  // only alternative and breaks row identity across add/remove. Kept in lockstep
  // with `draftFilters` (same length/order). Mirrors QueryBuilderPage's filterKeys.
  const [filterKeys, setFilterKeys] = useState<number[]>([]);
  const nextFilterKey = useRef(0);

  const { data: queryDef } = useQuery({
    queryKey: ['query', queryId],
    queryFn: () => queryService.getById(queryId),
    enabled: Number.isFinite(queryId)
  });

  const {
    data: results,
    isFetching,
    error
  } = useQuery({
    queryKey: ['queryExec', queryId, page, pageSize, sortField, sortDirection, JSON.stringify(appliedFilters)],
    queryFn: () =>
      queryService.execute(queryId, {
        page,
        pageSize,
        sort: sortField ? { field: sortField, direction: sortDirection === 'ASC' ? 'asc' : 'desc' } : undefined,
        adHocFilters: appliedFilters.length > 0 ? appliedFilters : undefined
      }),
    enabled: Number.isFinite(queryId),
    staleTime: 0
  });

  const fieldLabel = (entity: string, field: string): string =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.label ?? field;
  const entityLabel = (entity: string): string => schemaEntities.find((e) => e.key === entity)?.label ?? entity;
  const fieldFor = (entity: string, field: string): QueryField | undefined =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field);

  const columns: TableColumn[] = useMemo(() => {
    if (!queryDef) return [];
    return [...queryDef.columns]
      .sort((a, b) => a.order - b.order)
      .map((col) => ({
        key: `${col.entity}.${col.field}`,
        label: `${fieldLabel(col.entity, col.field)} (${entityLabel(col.entity)})`,
        defaultVisible: true,
        // To-many derived aggregates can't be sorted server-side (#919).
        sortable: fieldFor(col.entity, col.field)?.sortable !== false
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDef, schemaEntities]);

  const executedSql = results?.sql ?? queryDef?.lastExecutedSql ?? null;
  const sqlPersistError = results?.sqlPersistError ?? null;
  const rows = results?.rows ?? [];
  const totalItems = results?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Editing a Global query is Admin-only; Personal queries are only viewable by
  // their owner, so any personal query reachable here is the user's own to edit.
  const canEdit = queryDef ? queryDef.type === 'personal' || permissions.isAdmin : false;

  const handleSort = (columnKey: string): void => {
    if (columnKey === sortField) {
      setSortDirection((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(columnKey);
      setSortDirection('ASC');
    }
  };

  const handleExport = async (): Promise<void> => {
    if (!queryDef) return;
    setIsExporting(true);
    try {
      await downloadCsv(queryDef.name);
    } finally {
      setIsExporting(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      await deleteQuery(queryId);
      setShowDelete(false);
      navigate('/queries');
    } catch (err) {
      // deleteMutation has no onError and ConfirmDialog fires onConfirm without
      // awaiting — without this catch a failed delete leaves the dialog stuck and
      // throws an unhandled rejection. Surface the reason and close the dialog.
      setShowDelete(false);
      showToast.error(getApiErrorMessage(err, 'Failed to delete query'));
    }
  };

  const fieldType = (entity: string, field: string): QueryFieldType =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.type ?? 'text';

  const fieldOptions = (entity: string, field: string): string[] =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.options ?? [];

  // Seed a filter's value so the dropdown's displayed default is actually stored.
  // Boolean/enum controls show a first option the user may never click — without
  // seeding, the value stays undefined and the backend treats the filter as
  // "no filter", returning the whole dataset (bug 868). Text/number/date start
  // empty so the user must type a value.
  const defaultValueForField = (entity: string, field: string): QueryCellValue | undefined => {
    const type = fieldType(entity, field);
    if (type === 'boolean') return true;
    if (type === 'enum') return fieldOptions(entity, field)[0];
    return undefined;
  };

  // When the field changes, the previously chosen operator may no longer be valid
  // for the new field type (e.g. "Date range" on an enum field), which would leave
  // stale date pickers on screen and point the operator Select at an option
  // outside its list. Keep the operator if still valid, otherwise fall back to the
  // first one for the new type, and re-seed the value/valueTo. Mirrors the
  // builder's reconcileForField (QueryBuilderPage) so the two pages stay in step.
  const reconcileForField = (entity: string, field: string, current: QueryFilterOperator): Partial<QueryFilter> => {
    const operators = OPERATORS_BY_FIELD_TYPE[fieldType(entity, field)];
    return {
      operator: operators.includes(current) ? current : operators[0],
      value: defaultValueForField(entity, field),
      valueTo: undefined
    };
  };

  // The query's own columns are the fields available for ad-hoc filtering, minus
  // any the schema marks non-filterable (#919).
  const filterableFields = useMemo(
    () =>
      queryDef
        ? [...queryDef.columns]
            .sort((a, b) => a.order - b.order)
            .filter((c) => fieldFor(c.entity, c.field)?.filterable !== false)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryDef, schemaEntities]
  );

  const addDraftFilter = (): void => {
    const first = filterableFields[0];
    if (!first) return;
    setDraftFilters((prev) => [
      ...prev,
      {
        entity: first.entity,
        field: first.field,
        operator: 'equals',
        value: defaultValueForField(first.entity, first.field)
      }
    ]);
    setFilterKeys((keys) => [...keys, nextFilterKey.current++]);
  };

  const updateDraftFilter = (index: number, patch: Partial<QueryFilter>): void => {
    // Clear a stale validation banner as soon as the user starts fixing the filter.
    setFilterError(null);
    setDraftFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const removeDraftFilter = (index: number): void => {
    setFilterError(null);
    setDraftFilters((prev) => prev.filter((_, i) => i !== index));
    setAppliedFilters((prev) => prev.filter((_, i) => i !== index));
    setFilterKeys((keys) => keys.filter((_, i) => i !== index));
    setPage(1);
  };

  const applyFilters = (): void => {
    const validationError = validateFilters(draftFilters, schemaEntities);
    if (validationError) {
      setFilterError(validationError);
      return;
    }
    setFilterError(null);
    setPage(1);
    setAppliedFilters(draftFilters);
  };

  const clearFilters = (): void => {
    setFilterError(null);
    setDraftFilters([]);
    setFilterKeys([]);
    setAppliedFilters([]);
    setPage(1);
  };

  const renderCell = (column: TableColumn, row: QueryResultRow): React.ReactNode => {
    const [entity, field] = column.key.split('.');
    const value = row[column.key];
    const link = ENTITY_LINK[entity];
    const entityId = row[`${entity}.id`];
    // US-10: render a link only when this is a link-bearing field, the row has an
    // id, a value, AND the viewer is permitted to open that entity — else plain text.
    const canView = link ? permissions[link.permission] === true : false;

    if (
      link &&
      canView &&
      link.fields.includes(field) &&
      entityId !== null &&
      entityId !== undefined &&
      value !== null &&
      value !== ''
    ) {
      return (
        <Link to={`${link.path}/${entityId}`} className={styles.entityLink}>
          {displayValue(value, entity, field)}
        </Link>
      );
    }
    return displayValue(value, entity, field);
  };

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Failed to run query"
          message={getApiErrorMessage(error, 'An error occurred while executing the query.')}
          action={
            <Button variant="primary" onClick={() => navigate('/queries')}>
              <ArrowLeft size={16} />
              Back to Queries
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<Search size={24} />}
        title={queryDef?.name ?? 'Query Results'}
        backLabel="Go Back"
        onBack={() => navigate('/queries')}
        actions={
          <>
            {queryDef && <QueryTypeBadge type={queryDef.type} />}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowSql((s) => !s)}
              disabled={!executedSql}
              title={executedSql ? 'View the SQL that ran' : 'Run the query to see its SQL'}
            >
              <Code2 size={16} />
              {showSql ? 'Hide SQL' : 'View SQL'}
            </Button>
            <Button variant="primary" size="sm" loading={isExporting} onClick={handleExport} disabled={!queryDef}>
              <Download size={16} />
              Export CSV
            </Button>
            {canEdit && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(`/queries/${queryId}/edit`, { state: { returnTo: `/queries/${queryId}/results` } })
                  }
                >
                  <Pencil size={16} />
                  Edit
                </Button>
                <Button variant="danger" size="sm" onClick={() => setShowDelete(true)} disabled={!queryDef}>
                  <Trash2 size={16} />
                  Delete
                </Button>
              </>
            )}
          </>
        }
      />

      {showSql && executedSql && (
        <SqlView sql={executedSql} error={sqlPersistError} />
      )}

      {queryDef && filterableFields.length > 0 && (
        <div className={styles.filterBar} data-test-id="query-adhoc-filters">
          {draftFilters.map((filter, index) => {
            const operators = OPERATORS_BY_FIELD_TYPE[fieldType(filter.entity, filter.field)];
            return (
              <div key={filterKeys[index]} className={styles.filterRow}>
                <Select
                  options={filterableFields.map((f) => ({
                    value: `${f.entity}.${f.field}`,
                    label: `${fieldLabel(f.entity, f.field)} (${entityLabel(f.entity)})`
                  }))}
                  value={`${filter.entity}.${filter.field}`}
                  onChange={(value) => {
                    const [entity, field] = value.split('.');
                    // Switching fields may change the type (e.g. text → enum), so
                    // reconcile the operator to one valid for the new type and drop
                    // any lingering value, seeding the new field's default —
                    // otherwise a stale dateRange operator survives onto a
                    // non-date field (rendering dead date pickers) and a stale
                    // free-text value hides behind a dropdown.
                    updateDraftFilter(index, {
                      entity,
                      field,
                      ...reconcileForField(entity, field, filter.operator)
                    });
                  }}
                />
                <Select
                  options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] }))}
                  value={filter.operator}
                  onChange={(value) => updateDraftFilter(index, { operator: value as QueryFilterOperator })}
                />
                <FilterValueInput
                  filter={filter}
                  fieldType={fieldType(filter.entity, filter.field)}
                  options={fieldOptions(filter.entity, filter.field)}
                  getOptionLabel={(opt) => getQueryFieldLabel(filter.entity, filter.field, opt)}
                  onChange={(patch) => updateDraftFilter(index, patch)}
                />
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => removeDraftFilter(index)}
                  title="Remove filter"
                  aria-label="Remove filter"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            );
          })}
          <div className={styles.filterActions}>
            <Button variant="secondary" size="sm" onClick={addDraftFilter}>
              <Plus size={16} />
              Add filter
            </Button>
            <Button variant="primary" size="sm" onClick={applyFilters} disabled={draftFilters.length === 0}>
              Apply filters
            </Button>
            {appliedFilters.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      {filterError && (
        <div className={styles.errorBanner} role="alert">
          <AlertCircle size={18} className={styles.errorBannerIcon} aria-hidden />
          <span>{filterError}</span>
        </div>
      )}

      {!queryDef ? (
        <LoadingState message="Loading query..." />
      ) : (
        <div className={styles.tableScroll}>
          <RZTable<QueryResultRow>
            data={rows}
            columns={columns}
            getRowKey={(row) => rows.indexOf(row)}
            renderCell={renderCell}
            sorting={{
              sortConfig: { field: sortField ?? '', direction: sortDirection },
              onSort: handleSort,
              isSorted: (columnKey) => columnKey === sortField
            }}
            pagination={{
              currentPage: page,
              totalPages,
              totalItems,
              pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
              itemName: 'rows',
              pageSizeOptions: [...QUERY_PAGE_SIZES]
            }}
            state={{
              isLoading: isFetching && rows.length === 0,
              isFetching,
              isEmpty: totalItems === 0,
              emptyTitle: 'No results',
              emptyDescription: 'This query returned no rows.'
            }}
          />
        </div>
      )}

      <ConfirmDialog
        isOpen={showDelete}
        title="Delete Query"
        message="Are you sure you want to permanently delete this query?"
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDelete(false)}
      />
    </PageLayout>
  );
};

export default QueryResultsPage;
